<template>
  <div class="res-info-card">
    <div class="res-info-card__top">
      <button v-if="canShowDelete" @click="onCardCancel" type="button" class="res-info-card__delete">
        <img src="@/assets/img/clear-small-gray-icon.svg" alt="">
      </button>
      <app-button
        v-if="clearable && room.is_cancelled"
        @click.native="onCardRestore"
        size="small"
        class="res-info-card__delete res-info-card__delete--restore"
      >
        Restore
      </app-button>
      <div class="res-info-card__status" :style="{ backgroundColor: statusColor }">{{ statusLabel }}</div>
      <div class="res-info-card__count">{{ count }}.</div>
      <div class="res-info-card__accomodation_type">
        <app-room-icon :accomodation_type="room.accomodation_type" :disabled="true"/>
      </div>
      <div class="res-info-card__info">
        <div class="res-info-card__name">{{ room.hotel_room.name }} ({{ nights }} nights)</div>
        <div v-if="!property" class="res-info-card__early-late">
          <app-checkbox v-model="room.early_check_in" label="Early"/>
          <app-form-group
            label="Check-in time"
            label-for="early_check_in_time_form"
            class="form-group--no-indent"
            label_left
            :disabled="!room.early_check_in"
          >
            <app-input
              v-model="room.early_check_in_time"
              v-mask="'99:99'"
              placeholder="--:--"
              autocomplete="off"
              id="early_check_in_time_form"
              name="early_check_in_time_form"
              :disabled="!room.early_check_in"
              class="input--65"
            />
          </app-form-group>
          <app-checkbox v-model="room.late_check_out" label="Late"/>
          <app-form-group
            label="Check-out time"
            label-for="late_check_out_time_form"
            class="form-group--no-indent"
            label_left
            :disabled="!room.late_check_out"
          >
            <app-input
              v-model="room.late_check_out_time"
              v-mask="'99:99'"
              placeholder="--:--"
              autocomplete="off"
              id="late_check_out_time_form"
              name="late_check_out_time_form"
              :disabled="!room.late_check_out"
              class="input--65"
            />
          </app-form-group>
        </div>
      </div>
    </div>
    <div class="res-info-card__times">
      <app-form-group label="Check-in">
        <app-date-picker
          v-model="room.check_in"
          placeholder="Check-in"
          :clearable="false"
          :readonly="readonlyCheckIn"
        />
      </app-form-group>
      <app-form-group label="Check-out">
        <app-date-picker
          v-model="room.check_out"
          placeholder="Check-out"
          :readonly="readonlyCheckOut"
          :default-value="room.check_in"
          :clearable="false"
        />
      </app-form-group>
      <app-form-group label="Meal plan">
        <app-select
          v-model="room.meal_plan"
          :options="mp_options"
          :clearable="false"
          placeholder="Meal plan"
          class="select--160"
        />
      </app-form-group>
      <app-form-group label="Count" label-for="count">
        <app-input
          ref="countInput"
          @input="onCountInput"
          @blur.native="onCountBlur($event)"
          :value="room.count"
          :key="'count_'+countInputKey"
          :min="minCount"
          type="number"
          class="input--45 input--center"
          id="count"
        />
      </app-form-group>
    </div>
  </div>
</template>

<script>
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import isEqual from 'lodash.isequal'

const STATUS_MAP = {
  new: { label: 'New', color: '#77b017' },
  changed: { label: 'Changed', color: '#ff8426' },
  cancelled: { label: 'Cancelled', color: 'red' },
  original: { label: 'Original', color: '#35a4ea' },
}

export default {
  name: 'AppRoomsEdit',
  components: { AppRoomIcon },
  props: {
    clearable: { type: Boolean, default: true },
    count: { type: Number, default: 1 },
    mp_options: { type: Array, default: () => [] },
    room: { type: Object, default: () => ({}) },
    property: { type: Object, default: null },
  },
  data() {
    return {
      originalRoom: {},
      isChanged: false,
      countInputKey: 0,
    }
  },
  computed: {
    minCount() {
      const m = Number(this.updateRestrictions.min_count)
      return Number.isFinite(m) && m > 0 ? m : 1
    },
    updateRestrictions() {
      return this.room?.hotel_management_short_room?.update_restrictions || {}
    },
    canShowDelete() {
      return (this.clearable || !this.room.is_cancelled) && !this.updateRestrictions.delete
    },
    readonlyCheckIn() {
      return !!this.updateRestrictions.check_in
    },
    readonlyCheckOut() {
      return !this.room.check_in || !!this.updateRestrictions.check_out
    },
    statusKey() {
      if (!this.room.id) return 'new'
      if (this.room.is_cancelled) return 'cancelled'
      if (this.isChanged) return 'changed'
      return 'original'
    },
    statusConfig() {
      return STATUS_MAP[this.statusKey] || STATUS_MAP.original
    },
    statusLabel() {
      return this.statusConfig.label
    },
    statusColor() {
      return this.statusConfig.color
    },
    nights() {
      const ci = this.room.check_in
      const co = this.room.check_out
      if (!ci || !co) return ''
      const d1 = new Date(ci)
      const d2 = new Date(co)
      const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24))
      return diff < 0 ? 0 : diff
    },
  },
  watch: {
    room: {
      deep: true,
      immediate: true,
      handler(newRoom, oldRoom) {
        const newId = newRoom && newRoom.id
        const oldId = oldRoom && oldRoom.id
        const baseId = this.originalRoom && this.originalRoom.id

        if (newId && newId !== baseId && newId !== oldId) {
          try {
            this.originalRoom = JSON.parse(JSON.stringify(newRoom))
          } catch (e) {
            this.originalRoom = { ...newRoom }
          }
          this.isChanged = false
          return
        }
        this.isChanged = !isEqual(newRoom, this.originalRoom)
      }
    },
  },
  methods: {
    onCountInput(raw) {
      if (raw === null || raw === undefined) {
        this.room.count = ''
        return
      }

      const val = String(raw).trim()
      if (val === '') {
        this.room.count = ''
        return
      }

      const n = Number(val)
      if (!Number.isFinite(n)) return

      this.room.count = Math.floor(n)
    },
    onCountBlur() {
      if (this.room.count === '' || this.room.count === null || String(this.room.count).trim() === '') {
        this.room.count = this.minCount
        this.countInputKey++
        return
      }

      const n = Number(this.room.count)
      if (!Number.isFinite(n) || n < this.minCount) {
        this.room.count = this.minCount
        this.countInputKey++
        return
      }

      const floored = Math.floor(n)
      if (floored !== this.room.count) {
        this.room.count = floored
        this.countInputKey++
      }
    },
    onCardCancel() {
      if (this.room.id) this.$emit('cardCancel')
      else this.$emit('cardDelete')
    },
    onCardRestore() {
      this.$emit('cardRestore')
    },
  },
}
</script>

<style scoped lang="sass">
.res-info-card
  position: relative
  border: 1px solid #eaeaea
  border-radius: 10px
  background-color: #fff

.res-info-card__top
  display: flex
  align-items: center
  gap: 12px
  padding: 12px 25px 12px 12px

.res-info-card__times
  display: flex
  gap: 10px
  padding: 5px 25px 5px 12px

.res-info-card__bottom
  padding: 12px 25px 12px 12px
  border-top: 1px solid #e7e7e7

.res-info-card__delete
  position: absolute
  top: 2px
  right: 2px
  padding: 10px
  line-height: 0

.res-info-card__delete--restore
  top: 5px
  right: 5px
  min-height: 26px

.res-info-card__count
  display: flex
  align-items: center
  justify-content: center
  width: 36px
  height: 35px
  border-radius: 8px
  background-color: #eee
  color: #181818
  font-size: 12px
  font-weight: 600

.res-info-card__name
  color: #000
  font-size: 14px
  font-weight: 600

.res-info-card__early-late
  display: flex
  align-items: center
  gap: 30px
  margin-top: 5px

.res-info-card__status
  position: absolute
  bottom: 5px
  right: 5px
  padding: 5px 10px 4px
  border-radius: 15px
  font-size: 10px
  color: #fff
</style>
